import React, { useCallback, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import BaseModalComponent, { StyledBaseModal, StyledBaseModalContent } from '../BaseModal.component';
import { useTranslation } from 'react-i18next';

import FormLayoutComponent from '../../Form/FormLayout.component';
import SelectComponent from '../../Input/Select.component';
import InputComponent from '../../Input/Input.component';
import FileComponent from '../../Input/File.component';
import { useAppDispatch } from '../../../hooks/useAppDispatcher.hook';
import { getAttachmentTypesThunk } from '../../../redux/Attachment/Attachment.thunk';
import toTitle from '../../../utils/toTitle';
import ButtonComponent, { StyledButton } from '../../Button.component';
import { useAppSelector } from '../../../hooks/useAppSelector.hook';
import { getSelectedAttachment } from '../../../redux/Attachment/Attachment.slice';
import { uuidv4 } from '../../../utils/uuid';
import IconButtonComponent from '../../IconButton.component';
import TrashIcon from '../../Icons/Trash.icon';
import { StyledBaseIcon } from '../../Icons/Base.icon';
import {
  fileValidation,
  regExp,
  required,
  validateIf,
} from '../../../services/validator.service';
import { useModalManager } from '../../../hooks/ModalManager.hook';
import AttachmentRemoveAlertComponent from '../../Alert/Attachment/AttachmentRemoveAlert.component';
import { ALERT_ACTION_ENUM } from '../../../types/action.types';
import { Dropzone } from '../../Dropzone.component';
import {
  ATTACHMENT_ACCEPTED_EXTENSIONS,
  ATTACHMENT_ACCEPTED_MIME_FORMATS,
  ATTACHMENT_MAX_FILE_SIZE,
} from '../../../constants/attachment.constant';
import useFormHook from '../../../hooks/useForm.hook';

interface AttachmentEditModalProps
  extends React.AllHTMLAttributes<HTMLDivElement> {
  handleChange?: Function;
  handleClose?: () => void;
  file?: File;
}

const StyledAttachmentEditFooterRight = styled.div`
  display: flex;
  gap: 10px;
`;
const StyledAttachmentEditFooterLeft = styled.div`
  ${StyledBaseIcon}, & > *:hover ${StyledBaseIcon} {
    color: ${({ theme }) => theme.colors.RED.R400};
  }
`;
const StyledAttachmentEditFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 10px 0 0 0;
  gap: 10px;

  ${StyledButton} {
    min-width: 100px;
  }
`;
const StyledAttachmentFormStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;
const StyledAttachmentForm = styled.div``;
const StyledAttachmentEdit = styled.div`
  ${StyledBaseModal} {
    width: 400px;
  }

  ${StyledBaseModalContent} {
    overflow-y: visible;
  }
`;

const AttachmentEditModal = ({
  file,
  handleChange,
  handleClose,
}: AttachmentEditModalProps) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const selected = useAppSelector(getSelectedAttachment);
  const { form, registerField, setFormValue, validate, handleFormChange } =
    useFormHook({
      uuid: undefined,
      author_id: 0,
      tmpUUID: undefined,
      type: '',
      url: undefined,
      file: file || undefined,
      comment: undefined,
      isPrivate: false,
    });
  const { openModal, closeModal } = useModalManager();

  useEffect(() => {
    if (!selected.item) return;

    const newForm = {
      ...selected.item,
      type: {
        value: selected.item.type,
        label: t(toTitle(selected.item.type)),
      },
      tmpUUID: selected.item.tmpUUID || selected.item.uuid,
      file:
        selected.item?.file ||
        (!!selected.item.uuid &&
          !!selected.item.url &&
          !selected.item.file &&
          selected.item.isCloud && {
            name: selected.item.url.substring(
              selected.item.url.lastIndexOf('/') + 1,
            ),
          }),
    };

    setFormValue(newForm);
  }, [selected]);

  const handleSaveResult = useCallback(() => {
    if (!validate()) return;

    handleChange &&
      handleChange({
        ...form,
        tmpUUID: form.tmpUUID || uuidv4(),
        file: form.file instanceof File ? form.file : undefined,
      });
    closeModal();
    handleClose && handleClose();
  }, [closeModal, form, handleChange, handleClose, validate]);

  const attachmentTypesLoadOptions = useCallback(async () => {
    const {
      payload: { data },
    } = await dispatch(getAttachmentTypesThunk(undefined));
    return {
      options: data.map((item: string) => ({
        value: item,
        label: t(toTitle(item)),
      })),
      hasMore: false,
    };
  }, []);

  const handleConfirmDelete = (action?: ALERT_ACTION_ENUM) => {
    if (!action || action !== ALERT_ACTION_ENUM.YES) return;
    handleChange &&
      handleChange({
        ...form,
        file: form.file instanceof File ? form.file : undefined,
        tmpUUID: form.uuid || form.tmpUUID,
        isDeleted: true,
      });
    closeModal();
    handleClose && handleClose();
  };

  const handleCancel = () => {
    closeModal();
    handleClose && handleClose();
  };

  const onDrop = (files: File[]) => {
    setFormValue({
      file: files[0],
      url: !files[0] || (!!files[0] && !form.file) ? undefined : form.url,
    });
  };

  const renderHeader = useMemo(() => {
    if (!form || (!form.uuid && !form.tmpUUID)) return t('attach.modalNew');
    return t('attach.modalEdit');
  }, [form]);

  const renderFooter = useMemo(() => {
    return (
      <StyledAttachmentEditFooter>
        <StyledAttachmentEditFooterLeft>
          {(form.uuid || form.tmpUUID) && (
            <IconButtonComponent
              onClick={() =>
                openModal(AttachmentRemoveAlertComponent, {
                  handleConfirm: handleConfirmDelete,
                })
              }
            >
              <TrashIcon />
            </IconButtonComponent>
          )}
        </StyledAttachmentEditFooterLeft>
        <StyledAttachmentEditFooterRight>
          <ButtonComponent appearance="primary" handleClick={handleSaveResult}>
            {t('Next')}
          </ButtonComponent>

          <ButtonComponent handleClick={handleCancel}>
            {t('Cancel')}
          </ButtonComponent>
        </StyledAttachmentEditFooterRight>
      </StyledAttachmentEditFooter>
    );
  }, [form]);

  const renderForm = useMemo(
    () => (
      <StyledAttachmentForm>
        <FormLayoutComponent>
          <StyledAttachmentFormStack>
            <SelectComponent
              {...registerField('type', {
                validations: [required],
              })}
              value={form.type}
              label={t('Type') + '*'}
              placeholder={t('Choose type')}
              isSearchable={false}
              loadOptions={attachmentTypesLoadOptions}
              onChangeOption={(type: unknown) => {
                handleFormChange({ type });
              }}
            />

            <InputComponent
              {...registerField('url', {
                validations: [
                  validateIf(!form.file, required),
                ],
              })}
              value={form.url}
              label={t('Url')}
              placeholder={t('Enter link')}
              onChange={(url: unknown) => {
                handleFormChange({ url });
              }}
              disabled={!!form.file}
            />

            <FileComponent
              {...registerField('file', {
                validations: [
                  validateIf(!form.url, required),
                  fileValidation({
                    maxSize: ATTACHMENT_MAX_FILE_SIZE,
                    acceptedFormats: ATTACHMENT_ACCEPTED_MIME_FORMATS,
                  }),
                ],
              })}
              value={form.file}
              label={t('File')}
              accept={ATTACHMENT_ACCEPTED_EXTENSIONS}
              onChange={(file: unknown) => {
                const isResetUrl = !file || (file && !form.file);

                handleFormChange({
                  file,
                  url: isResetUrl ? undefined : form.url,
                });
              }}
              disabled={!!form.url && !form.file}
            />
          </StyledAttachmentFormStack>
        </FormLayoutComponent>
      </StyledAttachmentForm>
    ),
    [form],
  );

  return (
    <Dropzone onDrop={onDrop}>
      <StyledAttachmentEdit>
        <BaseModalComponent
          header={renderHeader}
          footer={renderFooter}
          onClose={handleClose}
        >
          {renderForm}
        </BaseModalComponent>
      </StyledAttachmentEdit>
    </Dropzone>
  );
};

export default AttachmentEditModal;
